import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import StatusBadge, { STATUS_META } from "@/components/StatusBadge";
import { ArrowLeft, ExternalLink, Download, MessageCircle, Trash2, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { canSeeAmount } from "@/lib/roles";
import { downloadInvoicePDF } from "@/lib/pdf";
import { statusTemplate, templateByKey, buildWaLink as buildWa, LANGUAGES, getSavedLang, saveLang } from "@/lib/waTemplates";
import JobCard from "@/components/JobCard";
import usePageTitle from "@/hooks/use-page-title";
import { fmtDate, fmtDateTime } from "@/lib/date";

const WORKFLOW = [
  "inquiry",
  "quotation",
  "confirmed",
  "design",
  "customer_approval",
  "design_approved",
  "printing",
  "finishing",
  "quality_check",
  "packing",
  "ready",
  "out_for_delivery",
  "delivered",
  "completed",
];

const DESIGNER_ALLOWED = ["design", "customer_approval", "design_approved"];

export default function OrderDetail() {
  const { id } = useParams();
  usePageTitle(id ? `Order ${id}` : "Order");
  const nav = useNavigate();
  const { user } = useAuth();
  const showAmount = canSeeAmount(user);
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isDesigner = user?.role === "designer";
  const isCustomer = user?.role === "customer";
  const canUploadFiles = ["designer", "admin", "super_admin"].includes(user?.role);
  const canDownloadFiles = ["designer", "admin", "super_admin", "sales_executive", "production_manager", "machine_operator"].includes(user?.role);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [waLang, setWaLang] = useState(getSavedLang());
  const today = new Date().toISOString().slice(0, 10);
  const [pay, setPay] = useState({ amount: "", mode: "cash", note: "", payment_date: today });
  const [files, setFiles] = useState([]);

  const load = () => api.get(`/orders/${id}`).then((r) => setData(r.data));
  const loadFiles = () =>
    api.get(`/orders/${id}/files`).then((r) => setFiles(r.data)).catch(() => setFiles([]));

  useEffect(() => {
    load();
    if (user && user.role !== "customer") loadFiles();
  }, [id, user]);

  const advance = async (status) => {
    try {
      await api.patch(`/orders/${id}/status`, { status, note: `Moved to ${status}` });
      load();
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const addPayment = async (e) => {
    e.preventDefault();
    try {
      await api.post("/payments", {
        order_id: id,
        amount: Number(pay.amount) || 0,
        mode: pay.mode,
        note: pay.note,
        payment_date: pay.payment_date,
      });
      setPay({ amount: "", mode: "cash", note: "", payment_date: today });
      load();
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
    if (!allowed.includes(file.type.toLowerCase())) {
      alert("Only JPEG, JPG, PNG, or PDF allowed");
      e.target.value = "";
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      const mb = (file.size / 1024 / 1024).toFixed(1);
      const subject = encodeURIComponent(`Large printable file for Order ${id}`);
      const body = encodeURIComponent(
        `Hi Brothers Advertising team,\n\nThe printable file "${file.name}" (${mb} MB) exceeds the 100MB upload limit.\n\nOrder: ${id}\n\nPlease find the file attached to this email.\n\nRegards,\n${user?.name || ""}`
      );
      const proceed = window.confirm(
        `File "${file.name}" is ${mb} MB — over the 100 MB upload limit.\n\nClick OK to open your email client and send it to print@brothersdigital.in with the file attached.`
      );
      if (proceed) {
        window.location.href = `mailto:print@brothersdigital.in?subject=${subject}&body=${body}`;
      }
      e.target.value = "";
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/orders/${id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      e.target.value = "";
      loadFiles();
    } catch (e2) {
      alert(formatApiError(e2));
    }
  };

  const downloadFile = async (f) => {
    try {
      const res = await api.get(`/orders/${id}/files/${f.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  const removeFile = async (f) => {
    if (!window.confirm(`Delete file "${f.filename}"?`)) return;
    try {
      await api.delete(`/orders/${id}/files/${f.id}`);
      loadFiles();
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const { order, payments } = data;
  const currentIdx = WORKFLOW.indexOf(order.status);
  const nextStatus = currentIdx >= 0 && currentIdx < WORKFLOW.length - 1 ? WORKFLOW[currentIdx + 1] : null;
  // Designer can only advance TO an allowed status
  const canAdvance = nextStatus && (!isDesigner || DESIGNER_ALLOWED.includes(nextStatus));
  const trackUrl = `${window.location.origin}/track/${order.id}`;

  // WhatsApp notify — customer phone lookup happens via order's customer
  const buildWaLink = () => {
    const msg = statusTemplate({
      lang: waLang, status: order.status,
      name: order.customer_name, id: order.id,
      amount: Math.round(order.estimated_cost || 0).toLocaleString("en-IN"),
    }) + `\nTrack: ${trackUrl}`;
    return buildWa(order.customer_phone, msg);
  };

  const changeLang = (l) => { setWaLang(l); saveLang(l); };

  return (
    <div className="space-y-6" data-testid="order-detail-root">
      <Link to="/orders" className="text-sm text-muted-foreground hover:text-[#FF6B00] inline-flex items-center gap-2">
        <ArrowLeft size={14} /> Orders
      </Link>

      <div className="border-b border-[hsl(var(--border))] pb-6 flex items-end justify-between">
        <div>
          <div className="overline mb-2 flex items-center gap-2">
            {order.product_category}
            {order.source === "web" && (
              <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded" data-testid="web-order-badge">
                🌐 Web · {order.shop_order_no}
              </span>
            )}
          </div>
          <div className="font-mono text-xs text-muted-foreground">{order.id}</div>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-1">
            {order.product_name}
          </h1>
          {order.razorpay_payment_id && (
            <div className="mt-1 text-[10px] font-mono text-emerald-700">💳 Razorpay: {order.razorpay_payment_id}</div>
          )}
        </div>
        <StatusBadge status={order.status} />
      </div>

      {/* Design files (web orders — uploaded by customer) */}
      {order.source === "web" && (
        <div className="card-swiss p-5" data-testid="web-design-files">
          <div className="overline mb-3">Customer's Design Files</div>
          {(order.design_files && order.design_files.length > 0) ? (
            <div className="space-y-2">
              {order.design_files.map((f, i) => (
                <a key={i} href={f.url} target="_blank" rel="noreferrer" className="flex items-center justify-between p-3 rounded-lg bg-[hsl(var(--muted))] hover:bg-orange-50 hover:text-[#FF6B00] transition-colors" data-testid={`design-file-${i}`}>
                  <div className="text-sm">
                    <div className="font-semibold">{f.filename || `file-${i + 1}`}</div>
                    <div className="text-[10px] text-muted-foreground">{f.uploaded_at ? fmtDateTime(f.uploaded_at) : ""} · {f.size ? `${Math.round(f.size / 1024)} KB` : ""}</div>
                  </div>
                  <Download size={16} />
                </a>
              ))}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">
              {order.design_upload_later
                ? "⏳ Customer opted to upload artwork later (within 7 days of confirmation)."
                : "No design files uploaded by the customer yet."}
            </div>
          )}
        </div>
      )}

      {/* Workflow bar */}
      <div className="card-swiss p-5" data-testid="workflow-bar">
        <div className="overline mb-4">Workflow</div>
        <div className="grid grid-cols-6 md:grid-cols-13 gap-2" style={{ gridTemplateColumns: `repeat(${WORKFLOW.length}, minmax(0,1fr))` }}>
          {WORKFLOW.map((s, i) => {
            const done = i <= currentIdx;
            const meta = STATUS_META[s];
            return (
              <div key={s} className="text-center">
                <div
                  className="h-2 mb-2"
                  style={{ background: done ? "#FF6B00" : "hsl(var(--border))" }}
                />
                <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: done ? meta.color : "hsl(var(--muted-foreground))" }}>
                  {meta.label}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          {!isCustomer && canAdvance && (
            <button onClick={() => advance(nextStatus)} className="btn-swiss btn-accent" data-testid="advance-status-btn">
              Advance → {STATUS_META[nextStatus]?.label}
            </button>
          )}
          {!isCustomer && isDesigner && (
            <div className="flex flex-wrap gap-2" data-testid="designer-status-buttons">
              {DESIGNER_ALLOWED.map((s) => (
                <button
                  key={s}
                  onClick={() => advance(s)}
                  className="btn-swiss btn-outline"
                  disabled={order.status === s}
                  data-testid={`designer-set-${s}`}
                >
                  Set: {STATUS_META[s]?.label}
                </button>
              ))}
            </div>
          )}
          {!isCustomer && !isDesigner && order.status !== "cancelled" && order.status !== "completed" && (
            <button
              onClick={() => advance("cancelled")}
              className="btn-swiss btn-outline"
              data-testid="cancel-order-btn"
            >
              Cancel Order
            </button>
          )}
          <a
            href={trackUrl}
            target="_blank"
            rel="noreferrer"
            className="btn-swiss btn-outline"
            data-testid="public-track-link"
          >
            Public Tracking <ExternalLink size={12} />
          </a>
          {showAmount && (
            <button
              onClick={() => downloadInvoicePDF({ order, payments })}
              className="btn-swiss btn-outline"
              data-testid="download-invoice-btn"
            >
              <Download size={14} /> Invoice PDF
            </button>
          )}
          {showAmount && !isCustomer && (
            <button
              onClick={async () => {
                await downloadInvoicePDF({ order, payments });
                const msg = templateByKey("invoice", {
                  lang: waLang, name: order.customer_name, id: order.id,
                  amount: Math.round(order.estimated_cost || 0).toLocaleString("en-IN"),
                }) + `\nTrack: ${trackUrl}`;
                window.open(buildWa(order.customer_phone, msg), "_blank");
              }}
              className="btn-swiss btn-outline"
              style={{ borderColor: "#25D366", color: "#25D366" }}
              data-testid="share-invoice-whatsapp-btn"
            >
              <MessageCircle size={14} /> Share Invoice on WhatsApp
            </button>
          )}
          {!isCustomer && (
            <a
              href={buildWaLink()}
              target="_blank"
              rel="noreferrer"
              className="btn-swiss btn-outline"
              style={{ borderColor: "#25D366", color: "#25D366" }}
              data-testid="whatsapp-notify-btn"
            >
              <MessageCircle size={14} /> Notify via WhatsApp
            </a>
          )}
          {!isCustomer && (
            <div className="inline-flex items-center gap-2 border border-[hsl(var(--border))] px-2 py-1.5" data-testid="wa-lang-selector">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Lang</span>
            {LANGUAGES.map((l) => (
              <button key={l.code} type="button" onClick={() => changeLang(l.code)}
                className={`text-xs px-1.5 ${waLang === l.code ? "text-[#FF6B00] font-bold underline underline-offset-4" : "text-muted-foreground hover:text-foreground"}`}
                data-testid={`wa-lang-${l.code}`}>
                {l.label}
              </button>
            ))}
            </div>
          )}
          {isAdmin && (
            <Link
              to={`/orders/${order.id}/edit`}
              className="btn-swiss btn-outline"
              data-testid="edit-order-btn"
            >
              <Pencil size={14} /> Edit Order
            </Link>
          )}
          {isAdmin && (
            <button
              onClick={async () => {
                if (!window.confirm(`Permanently delete order ${order.id}?\n\nAll payments and history will be removed. This cannot be undone.`)) return;
                try {
                  await api.delete(`/orders/${order.id}`);
                  nav("/orders");
                } catch (e) {
                  alert(formatApiError(e));
                }
              }}
              className="btn-swiss btn-outline text-red-500 border-red-500 hover:bg-red-50"
              data-testid="delete-order-btn"
            >
              <Trash2 size={14} /> Delete Order
            </button>
          )}
        </div>
      </div>

      {err && <div className="text-sm text-red-600">{err}</div>}

      {/* Printable Files (never for customers) */}
      {user?.role !== "customer" && (
        <div className="card-swiss p-5" data-testid="files-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">Printable Files</div>
              <div className="text-xs text-muted-foreground mt-1">
                JPEG, JPG, PNG, PDF · max 100 MB · files auto-delete after 15 days. Larger files → email <a href="mailto:print@brothersdigital.in" className="text-[#FF6B00]">print@brothersdigital.in</a>. Not visible to customers.
              </div>
            </div>
            {canUploadFiles && (
              <label className="btn-swiss btn-accent cursor-pointer" data-testid="upload-file-label">
                + Upload File
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                  onChange={uploadFile}
                  className="hidden"
                  data-testid="upload-file-input"
                />
              </label>
            )}
          </div>
          {files.length === 0 ? (
            <div className="text-sm text-muted-foreground py-4">No printable files uploaded yet.</div>
          ) : (
            <div className="divide-y divide-[hsl(var(--border))]">
              {files.map((f) => (
                <div key={f.id} className="flex items-center gap-3 py-2 text-sm" data-testid={`file-row-${f.id}`}>
                  <div className="w-8 h-8 border border-[hsl(var(--border))] flex items-center justify-center text-[10px] font-mono font-bold uppercase">
                    {(f.filename.split(".").pop() || "").slice(0, 4)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{f.filename}</div>
                    <div className="text-xs text-muted-foreground">
                      {(f.size / 1024 / 1024).toFixed(2)} MB · uploaded by {f.uploaded_by_name} · {fmtDateTime(f.uploaded_at)}
                      {f.expires_at && (
                        <> · <span className="text-[#FF6B00]">auto-deletes {fmtDate(f.expires_at)}</span></>
                      )}
                    </div>
                  </div>
                  {canDownloadFiles && (
                    <button
                      onClick={() => downloadFile(f)}
                      className="btn-swiss btn-outline"
                      data-testid={`download-file-${f.id}`}
                    >
                      <Download size={14} /> Download
                    </button>
                  )}
                  {canUploadFiles && (
                    <button
                      onClick={() => removeFile(f)}
                      className="p-1.5 hover:text-[#FF6B00]"
                      title="Delete"
                      data-testid={`delete-file-${f.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className={`card-swiss p-5 space-y-4 ${showAmount ? "lg:col-span-2" : "lg:col-span-3"}`}>
          <div className="overline">Order Details</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            {[
              ["Customer", order.customer_name],
              ["Company", order.customer_company || "—"],
              ["Priority", order.priority],
              ["Delivery Date", order.delivery_date ? fmtDate(order.delivery_date) : "—"],
              ["Lamination", order.lamination ? "Yes" : "No"],
              ["Delivery", order.delivery_required ? "Yes" : "No"],
            ].map(([k, v]) => (
              <div key={k}>
                <div className="overline">{k}</div>
                <div className="mt-1">{v}</div>
              </div>
            ))}
          </div>

          {order.items && order.items.length > 0 ? (
            <div className="space-y-3 pt-2" data-testid="order-items-list">
              <div className="overline flex items-center gap-2">
                Jobs ({order.items.length})
                <span className="text-[10px] font-normal text-muted-foreground normal-case tracking-normal">— each job advances independently</span>
              </div>
              {order.items.map((it, i) => (
                <JobCard
                  key={it.job_id || i}
                  it={it}
                  showAmount={showAmount}
                  orderId={order.id}
                  role={user?.role}
                  onChanged={load}
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div><div className="overline">Product</div><div>{order.product_name}</div></div>
              <div><div className="overline">Size</div><div>{order.width || order.height ? `${order.width}×${order.height} ${order.size_unit || "ft"}` : "—"}</div></div>
              <div><div className="overline">Quantity</div><div>{order.quantity}</div></div>
            </div>
          )}

          {order.shipping_address && (
            <div><div className="overline">Shipping Address</div><div className="mt-1 text-sm whitespace-pre-line">{order.shipping_address}</div></div>
          )}
          {order.remarks && (
            <div><div className="overline">Remarks</div><div className="mt-1 text-sm">{order.remarks}</div></div>
          )}
        </div>

        {showAmount && (
        <div className="card-swiss p-5 space-y-3">
          <div className="overline">Billing</div>
          <div className="flex justify-between text-sm">
            <span>Estimated Cost</span>
            <span className="font-mono font-bold">
              ₹{Math.round(order.estimated_cost || 0).toLocaleString("en-IN")}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span>Paid</span>
            <span className="font-mono">₹{Math.round(order.paid || 0).toLocaleString("en-IN")}</span>
          </div>
          <div className="flex justify-between text-sm border-t border-[hsl(var(--border))] pt-3">
            <span className="font-bold">Balance</span>
            <span className="font-mono font-bold text-[#FF6B00]">
              ₹{Math.round(order.balance || 0).toLocaleString("en-IN")}
            </span>
          </div>

          <form onSubmit={addPayment} className={`pt-4 border-t border-[hsl(var(--border))] space-y-3 ${isCustomer ? "hidden" : ""}`} data-testid="payment-form">
            <div className="overline">Record Payment</div>
            <input
              type="number"
              placeholder="Amount"
              required
              value={pay.amount}
              onChange={(e) => setPay({ ...pay, amount: e.target.value })}
              className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none focus:border-[#FF6B00]"
              data-testid="payment-amount-input"
            />
            <select
              value={pay.mode}
              onChange={(e) => setPay({ ...pay, mode: e.target.value })}
              className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none"
              data-testid="payment-mode-select"
            >
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="card">Card</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="cheque">Cheque</option>
            </select>
            <input
              type="date"
              value={pay.payment_date}
              onChange={(e) => setPay({ ...pay, payment_date: e.target.value })}
              className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none focus:border-[#FF6B00]"
              data-testid="payment-date-input"
            />
            <input
              placeholder="Note (optional)"
              value={pay.note}
              onChange={(e) => setPay({ ...pay, note: e.target.value })}
              className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none"
              data-testid="payment-note-input"
            />
            <button className="btn-swiss btn-accent w-full justify-center" data-testid="record-payment-btn">
              Record Payment
            </button>
          </form>
        </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card-swiss p-5" data-testid="timeline-card">
          <div className="overline mb-4">Timeline</div>
          <div className="space-y-3">
            {order.timeline?.slice().reverse().map((t, i) => (
              <div key={i} className="flex gap-3 text-sm">
                <div className="w-2 h-2 mt-1.5" style={{ background: STATUS_META[t.status]?.color || "#71717A" }} />
                <div className="flex-1">
                  <div className="font-semibold capitalize">{STATUS_META[t.status]?.label || t.status}</div>
                  <div className="text-xs text-muted-foreground">{t.note}</div>
                </div>
                <div className="text-xs text-muted-foreground font-mono">
                  {fmtDateTime(t.at)}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card-swiss p-5" data-testid="payments-list-card">
          <div className="overline mb-4">Payments ({payments.length})</div>
          <div className="divide-y divide-[hsl(var(--border))]">
            {payments.map((p) => (
              <div key={p.id} className="flex items-center py-2 text-sm">
                <div className="flex-1">
                  {showAmount ? (
                    <div className="font-mono font-bold">
                      ₹{Math.round(p.amount).toLocaleString("en-IN")}
                    </div>
                  ) : (
                    <div className="font-mono font-bold text-muted-foreground">•••••</div>
                  )}
                  <div className="text-xs text-muted-foreground">
                    {p.mode.toUpperCase()} · {p.note || "—"}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground font-mono">
                  {fmtDate(p.created_at)}
                </div>
              </div>
            ))}
            {payments.length === 0 && (
              <div className="text-sm text-muted-foreground py-4">No payments yet.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
